import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle2, Home, Calendar, CreditCard, MapPin, Users, DollarSign, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ConfirmedCustomAdventure {
  requestNumber: string;
  adventureName: string;
  businessName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  finalPrice: {
    total: number;
    currency: string;
    breakdown?: {
      basePrice: number;
      options: number;
      taxes?: number;
    };
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  selectedOptions: Record<string, any>;
  confirmedAt: string;
  expiresAt?: string; // Payment deadline
  paymentDeadline?: string;
}

export default function CustomAdventureConfirmation() {
  const navigate = useNavigate();
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.adventureConfirmation' });
  const { t: tCommon } = useTranslation('common');
  
  const [adventureDetails, setAdventureDetails] = useState<ConfirmedCustomAdventure | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get data from URL params (this would come from email link)
    // In production, this would likely fetch from API using requestNumber
    const data = searchParams.get('data');
    
    if (data) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(data));
        setAdventureDetails(parsedData);
      } catch (error) {
        console.error('Error parsing adventure confirmation data:', error);
      }
    }
    
    setLoading(false);
  }, [searchParams]);

  const getTotalParticipants = () => {
    if (!adventureDetails) return 0;
    return adventureDetails.participants.adults + adventureDetails.participants.children + adventureDetails.participants.students;
  };

  const handleProceedToPayment = () => {
    // Navigate to custom adventure payment page
    const paymentData = {
      ...adventureDetails,
      bookingType: 'custom-adventure'
    };
    
    navigate('/custom-adventure-payment', { 
      state: { adventureData: paymentData } 
    });
  };

  const isExpired = () => {
    if (!adventureDetails?.expiresAt) return false;
    return new Date() > new Date(adventureDetails.expiresAt);
  };

  const getTimeUntilExpiry = () => {
    if (!adventureDetails?.expiresAt) return null;
    const now = new Date();
    const expiry = new Date(adventureDetails.expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return null;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }
    
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
      </div>
    );
  }

  if (!adventureDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('notFound.title')}</h1>
          <p className="text-gray-600 mb-4">{t('notFound.description')}</p>
          <Button onClick={() => navigate('/')} className="bg-[#77A146] hover:bg-[#6a9139]">
            <Home className="h-4 w-4 mr-2" />
            {t('notFound.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  const expired = isExpired();
  const timeLeft = getTimeUntilExpiry();

  return (
    <div className="min-h-screen bg-[#F0F4F0] p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex items-center gap-2 bg-white hover:bg-gray-50"
          >
            <Home className="h-4 w-4" />
            {tCommon('actions.backHome')}
          </Button>
        </div>

        {/* Main Confirmation Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
          {/* Success Header */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <CheckCircle2 className="h-20 w-20 text-green-600" />
              <div className="absolute -top-1 -right-1 h-6 w-6 bg-green-600 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2 text-gray-900">
            {t('title')}
          </h1>
          <p className="text-center text-gray-600 mb-2">
            {t('subtitle')}
          </p>
          <p className="text-center text-lg font-semibold text-green-600 mb-6">
            {t('requestNumber')}: {adventureDetails.requestNumber}
          </p>

          {/* Status and Urgency */}
          <div className="flex flex-col items-center space-y-3 mb-8">
            <Badge variant="secondary" className="bg-green-100 text-green-800 px-4 py-2 text-sm">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {t('status.confirmed')}
            </Badge>
            
            {timeLeft && !expired && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-center">
                <div className="flex items-center justify-center text-amber-800 text-sm">
                  <Clock className="h-4 w-4 mr-2" />
                  {t('paymentDeadline', { time: timeLeft })}
                </div>
              </div>
            )}
            
            {expired && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center">
                <div className="text-red-800 text-sm font-medium">
                  {t('expired.message')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Adventure Details Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Adventure Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-[#77A146]" />
              {t('sections.adventure')}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">{t('fields.adventureName')}</p>
                <p className="font-medium text-gray-900">{adventureDetails.adventureName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.business')}</p>
                <p className="font-medium text-gray-900">{adventureDetails.businessName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.confirmedDate')}</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(adventureDetails.confirmedAt), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>

          {/* Date & Participants */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-[#77A146]" />
              {t('sections.dateParticipants')}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">{t('fields.adventureDates')}</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(adventureDetails.dateRange.startDate), 'MMM d, yyyy')} - {' '}
                  {format(new Date(adventureDetails.dateRange.endDate), 'MMM d, yyyy')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.participants')}</p>
                <div className="flex items-center text-gray-900">
                  <Users className="h-4 w-4 mr-2" />
                  <span className="font-medium">{getTotalParticipants()} {t('fields.total')}</span>
                </div>
                <div className="mt-1 text-sm text-gray-600 space-y-1">
                  {adventureDetails.participants.adults > 0 && (
                    <div>• {adventureDetails.participants.adults} {t('fields.adults')}</div>
                  )}
                  {adventureDetails.participants.children > 0 && (
                    <div>• {adventureDetails.participants.children} {t('fields.children')}</div>
                  )}
                  {adventureDetails.participants.students > 0 && (
                    <div>• {adventureDetails.participants.students} {t('fields.students')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Pricing */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-[#77A146]" />
            {t('sections.finalPricing')}
          </h2>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-medium text-gray-900">
                {t('fields.totalPrice')}:
              </span>
              <span className="text-3xl font-bold text-green-600">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: adventureDetails.finalPrice.currency
                }).format(adventureDetails.finalPrice.total)}
              </span>
            </div>
            
            {adventureDetails.finalPrice.breakdown && (
              <div className="text-sm text-gray-600 space-y-1 mt-3 pt-3 border-t border-green-200">
                <div className="flex justify-between">
                  <span>{t('fields.basePrice')}:</span>
                  <span>${adventureDetails.finalPrice.breakdown.basePrice}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('fields.options')}:</span>
                  <span>${adventureDetails.finalPrice.breakdown.options}</span>
                </div>
                {adventureDetails.finalPrice.breakdown.taxes && (
                  <div className="flex justify-between">
                    <span>{t('fields.taxes')}:</span>
                    <span>${adventureDetails.finalPrice.breakdown.taxes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">
            {t('sections.customerDetails')}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('fields.customerName')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.email')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.phone')}</p>
              <p className="font-medium text-gray-900">{adventureDetails.customerDetails.phone}</p>
            </div>
          </div>
        </div>

        {/* Selected Options */}
        {Object.keys(adventureDetails.selectedOptions).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">
              {t('sections.confirmedOptions')}
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(adventureDetails.selectedOptions).map(([key, value]) => (
                <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-gray-600">{key}:</span>
                  <span className="font-medium text-gray-900">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {!expired ? (
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('readyToPay.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('readyToPay.description')}
              </p>
              <Button
                onClick={handleProceedToPayment}
                size="lg"
                className="bg-[#77A146] hover:bg-[#6a9139] text-white px-8 py-3"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                {t('actions.proceedToPayment')}
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-700 mb-3">
                {t('expired.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('expired.description')}
              </p>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                size="lg"
                className="px-8 py-3"
              >
                {t('actions.contactBusiness')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}